import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'

/**
 * baseURL convention:
 *   VITE_API_BASE = "" (default)            → use relative URLs, Vite/Pages proxy /api/* to Worker
 *   VITE_API_BASE = "https://api.xxx.com"   → absolute, frontend hits backend directly
 *
 * โค้ดทั้งโปรเจ็คเรียกด้วย path เต็มที่เริ่มด้วย `/api/...` เพื่อให้ตรงกับ Cloudflare Worker route
 */
export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || '',
  timeout: 15_000,
  headers: {
    'Accept': 'application/json',
  },
})

// ---- Request interceptor: Authorization Bearer + Accept-Language จาก prefs ----
api.interceptors.request.use(async (cfg: InternalAxiosRequestConfig) => {
  // dynamic import เพื่อหลีก circular dep
  const { useAuthStore } = await import('@/stores/auth')
  const auth = useAuthStore()
  if (auth.token) {
    cfg.headers.set('Authorization', `Bearer ${auth.token}`)
  }
  try {
    const { usePrefsStore } = await import('@/stores/prefs')
    const prefs = usePrefsStore()
    const lang = prefs.language === 'en' ? 'en-US' : 'th-TH'
    cfg.headers.set('Accept-Language', lang)
  } catch {
    cfg.headers.set('Accept-Language', 'th-TH')
  }
  return cfg
})

// ---- Response interceptor: 401 → re-login อัตโนมัติแล้ว retry 1 ครั้ง ----
let isRefreshing = false
let pendingQueue: Array<() => void> = []

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const original = err.config as InternalAxiosRequestConfig & { _retried?: boolean }
    if (
      err?.response?.status === 401 &&
      !original?._retried &&
      // อย่า re-login ตอน login เอง (กัน infinite loop)
      !original?.url?.includes('/api/auth/telegram')
    ) {
      original._retried = true
      const { useAuthStore } = await import('@/stores/auth')
      const auth = useAuthStore()

      // ถ้ามี refresh กำลังทำงานอยู่ → รอ
      if (isRefreshing) {
        await new Promise<void>((r) => pendingQueue.push(r))
      } else {
        isRefreshing = true
        try {
          await auth.loginWithTelegram()
        } finally {
          isRefreshing = false
          pendingQueue.forEach((r) => r())
          pendingQueue = []
        }
      }

      // retry request ด้วย token ใหม่ (interceptor ใส่ให้)
      if (auth.token) return api(original)
    }
    return Promise.reject(err)
  },
)

/** flag สำหรับ dev — ใช้ mock data ตรง ๆ ใน frontend ไม่ยิง backend */
export const useMock = (import.meta.env.VITE_USE_MOCK ?? 'false') === 'true'
